import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Grid,
    Paper,
    Typography,
    IconButton,
    Tooltip,
    Breadcrumbs,
    Link,
    Button,
    Tabs,
    Tab,
    Chip,
    Divider,
    Stack,
    useTheme,
    CircularProgress,
} from '@mui/material';
import {
    ArrowBack,
    Home as HomeIcon,
    PowerSettingsNew as PowerIcon,
    PlayArrow as PlayIcon,
    Stop as StopIcon,
    Memory as MemoryIcon,
    Lock as LockIcon,
    AccessTime as AccessTimeIcon,
    SignalCellularAlt as SignalCellularAltIcon,
    Analytics as AnalyticsIcon,
    History as HistoryIcon,
    CheckCircleOutline as CheckCircleOutlineIcon,
    Settings as SettingsIcon,
    SatelliteAlt as SatelliteIcon,
    BarChart as BarChartIcon,
    OpenInNew as OpenInNewIcon,
    EventNote as EventNoteIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import ModoStatus from '../components/DeviceDetails/ModoStatus';
import DataVisualization from '../components/DeviceDetails/DataVisualization';
import LogViewer from '../components/DeviceDetails/LogViewer';
import ConfigTab from '../components/DeviceDetails/ConfigTab';
import GnssReceiversTab from '../components/DeviceDetails/GnssReceiversTab';
import { useDevices } from '../contexts/DevicesContext';
import StatusIndicator from '../components/common/StatusIndicator';
import MetricCard from '../components/common/MetricCard';
import TimeRangeSelector, { TimeRange } from '../components/common/TimeRangeSelector';
import { useDeviceRealtime } from '../hooks/useDeviceRealtime';
import { useReferences } from '../hooks/useReferences';
import ReferencesRow from '../components/DeviceDetails/ReferencesRow';
import EventsTab from '../components/DeviceDetails/EventsTab';
import { InterferenceDetail, UbxFlagsSource, AgcDetectionSource, IntegritySource } from '../types/Device';

const GRAFANA_BASE_URL = 'https://grafana.synchropal.com';

const GRAFANA_LINKS: Array<{ label: string; build: (mqttId: string) => string }> = [
    {
        label: 'Overview',
        build: (id) => `${GRAFANA_BASE_URL}/d/overview-dashboard/overview?orgId=1&from=now-1h&to=now&timezone=browser&var-device_id=${id}&var-ocxo_mode=1&refresh=5s`,
    },
    {
        label: 'Steering Inputs and OCXO Control',
        build: (id) => `${GRAFANA_BASE_URL}/d/gnss_ocxo_dashboard/gnss-and-ocxo-dashboard?orgId=1&from=now-1h&to=now&timezone=browser&var-device_id=${id}&var-ocxo_mode=1&var-deviation_type=allan_deviation&refresh=5s`,
    },
    {
        label: 'ADSB DTOE',
        build: (id) => `${GRAFANA_BASE_URL}/d/adsb-dtoe-dashboard/adsb-dtoe-dashboard?orgId=1&from=now-1h&to=now&timezone=browser&var-device_id=${id}&var-deviation_type=allan_deviation&refresh=5s`,
    },
    {
        label: 'ADSB Raw Messages',
        build: (id) => `${GRAFANA_BASE_URL}/d/adsb-raw-messages/adsb-raw-messages-dashboard?orgId=1&from=now-1h&to=now&timezone=browser&var-device_id=${id}&refresh=5s`,
    },
    {
        label: 'ADSB Server Reports',
        build: (id) => `${GRAFANA_BASE_URL}/d/adsb-server-reports/adsb-server-reports-dashboard?orgId=1&from=now-1h&to=now&timezone=browser&var-device_id=${id}&refresh=5s`,
    },
    {
        label: 'CGGTTS Dashboard',
        build: (id) => `${GRAFANA_BASE_URL}/d/cggtts_dashboard/cggtts-dashboard?orgId=1&from=now-1h&to=now&timezone=browser&var-device_id=${id}&var-satellite_id=G08&var-frequency_code=P3&refresh=5s`,
    },
];

// TabPanel component for tab content
interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`device-tabpanel-${index}`}
            aria-labelledby={`device-tab-${index}`}
            {...other}
            style={{ marginTop: 16 }}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
};

const DevicePage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const { devices, approveDevice, startSteering, stopSteering } = useDevices();
    
    // State
    const [tabIndex, setTabIndex] = useState(0);
    const [timeRange, setTimeRange] = useState<TimeRange>('1h');
    
    // Loading states
    const [isApproving, setIsApproving] = useState(false);
    const [isStarting, setIsStarting] = useState(false);
    const [isStopping, setIsStopping] = useState(false);
    
    const device = devices.find(d => d.id === parseInt(id || '0'));
    
    // Get real-time data for this device
    const { realtimeData } = useDeviceRealtime(device?.id || 0);
    const { references } = useReferences(device?.id || 0);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    const handleStartStop = async () => {
        if (!device) return;
        
        try {
            if (device.status === 'running' || device.status === 'starting') {
                setIsStopping(true);
                await stopSteering(device.id);
            } else if (device.status === 'idle_connected' || device.status === 'stopping') {
                setIsStarting(true);
                await startSteering(device.id);
            }
        } catch (err) {
            console.error('Start/Stop error:', err);
        } finally {
            setIsStarting(false);
            setIsStopping(false);
        }
    };

    const handleApprove = async () => {
        if (!device || device.status !== 'pending_approval') return;
        setIsApproving(true);
        try {
            await approveDevice(device.id);
        } catch (err) {
            console.error('Approve error:', err);
        } finally {
            setIsApproving(false);
        }
    };

    if (!device) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">Device not found</Typography>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/')}
                    sx={{ mt: 2 }}
                >
                    Back to Dashboard
                </Button>
            </Box>
        );
    }

    const deviceLockStatus = realtimeData.telemetry?.lock_status ?? device.latest_status?.lock_status ?? 'N/A';
    
    const deviceNGnss = realtimeData.telemetry?.n_gnss ?? device.latest_status?.n_gnss ?? 0;

    const getReceiverInterferenceStatus = (
        gnssDeviceId: number,
        details: InterferenceDetail[] | null
    ): 'ok' | 'jamming' | 'spoofing' => {
        if (!details || details.length === 0) return 'ok';

        let hasSpoofing = false;
        let hasJamming = false;
        const key = String(gnssDeviceId);

        for (const source of details) {
            if (source.source === 'ubx_flags') {
                const devData = (source as UbxFlagsSource).per_device?.[key];
                if (devData) {
                    if (devData.spoofing_state >= 2 || devData.spoof_det_state >= 2) hasSpoofing = true;
                    if (devData.jamming_state >= 2) hasJamming = true;
                }
            } else if (source.source === 'agc_detection') {
                const devData = (source as AgcDetectionSource).per_device?.[key];
                if (devData?.jamming) hasJamming = true;
            } else if (source.source === 'integrity') {
                const affected = (source as IntegritySource).affected_devices ?? [];
                if (affected.includes(gnssDeviceId)) hasJamming = true;
            }
        }

        if (hasSpoofing) return 'spoofing';
        if (hasJamming) return 'jamming';
        return 'ok';
    };

    const lastConnectedTime = device.last_connected 
        ? format(new Date(device.last_connected), 'MMM d, yyyy HH:mm:ss')
        : 'Never';

    return (
        <Box>
            {/* Header with breadcrumbs and actions */}
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 3,
                    borderRadius: 3,
                    background: 'linear-gradient(90deg, rgba(30, 42, 58, 0.8) 0%, rgba(28, 39, 54, 0.9) 100%)',
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                        {/* Breadcrumbs */}
                        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                            <Link
                                underline="hover"
                                color="inherit"
                                href="/"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate('/');
                                }}
                                sx={{ display: 'flex', alignItems: 'center' }}
                            >
                                <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
                                Dashboard
                            </Link>
                            <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{mr: 0.5}}>
                                    <StatusIndicator 
                                        status={
                                            device.status === 'running' ? 'success' :
                                            device.status === 'idle_connected' ? 'info' :
                                            device.status === 'pending_approval' ? 'warning' :
                                            device.status === 'error' ? 'error' :
                                            'neutral'
                                        } 
                                        size={8} 
                                        tooltip={device.status.replace('_', ' ')}
                                    />
                                </Box>
                                {device.name}
                            </Typography>
                        </Breadcrumbs>

                        {/* Device Title */}
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h3" component="h1" sx={{textTransform: 'capitalize'}}>
                                {device.name} 
                            </Typography>
                            <Chip
                                label={device.status.replace('_', ' ')}
                                color={
                                    device.status === 'running' ? 'success' :
                                    device.status === 'starting' ? 'info' :
                                    device.status === 'idle_connected' ? 'info' :
                                    device.status === 'pending_approval' ? 'warning' :
                                    device.status === 'error' ? 'error' :
                                    device.status === 'offline' ? 'default' :
                                    device.status === 'booting' ? 'default' :
                                    'default'
                                }
                                size="small"
                                sx={{ ml: 2, textTransform: 'capitalize' }}
                            />
                        </Box>

                        {/* Device Info - MQTT ID could be useful here */}
                        <Typography variant="body1" color="text.secondary">
                           MQTT ID: {device.mqtt_id} • Last seen: {lastConnectedTime}
                        </Typography>
                    </Box>

                    {/* Action Buttons - MODIFIED based on device.status */}
                    <Stack direction="row" spacing={2}>
                        {device.status === 'pending_approval' && (
                            <Button
                                variant="contained"
                                startIcon={isApproving ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineIcon />}
                                color="primary"
                                onClick={handleApprove}
                                disabled={isApproving}
                            >
                                {isApproving ? 'Approving...' : 'Approve Connection'}
                            </Button>
                        )}
                        {(device.status === 'idle_connected' || device.status === 'stopping') && (
                            <Button
                                variant="contained"
                                startIcon={isStarting ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />}
                                color={'success'}
                                onClick={handleStartStop}
                                disabled={isStarting || isApproving || isStopping}
                            >
                                {isStarting ? 'Starting...' : 'Start Steering'}
                            </Button>
                        )}
                        {(device.status === 'running' || device.status === 'starting') && (
                            <Button
                                variant="contained"
                                startIcon={isStopping ? <CircularProgress size={16} color="inherit" /> : <StopIcon />}
                                color={'error'}
                                onClick={handleStartStop}
                                disabled={isStopping || isApproving || isStarting}
                            >
                                {isStopping ? 'Stopping...' : 'Stop Steering'}
                            </Button>
                        )}
                    </Stack>
                </Box>
            </Paper>

            {/* Tabs Navigation */}
            <Tabs
                value={tabIndex}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="device tabs"
                sx={{
                    mb: 3,
                    '& .MuiTabs-indicator': {
                        height: 3,
                        borderRadius: '3px 3px 0 0',
                    },
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        minHeight: 48,
                    },
                }}
            >
                <Tab 
                    icon={<AnalyticsIcon />} 
                    iconPosition="start" 
                    label="Overview" 
                    id="device-tab-0"
                    aria-controls="device-tabpanel-0"
                />
                <Tab 
                    icon={<SignalCellularAltIcon />} 
                    iconPosition="start" 
                    label="Monitoring" 
                    id="device-tab-1"
                    aria-controls="device-tabpanel-1"
                />
                <Tab 
                    icon={<SatelliteIcon />} 
                    iconPosition="start" 
                    label="GNSS Receivers" 
                    id="device-tab-2"
                    aria-controls="device-tabpanel-2"
                />
                <Tab 
                    icon={<SettingsIcon />} 
                    iconPosition="start" 
                    label="Configuration" 
                    id="device-tab-3"
                    aria-controls="device-tabpanel-3"
                />
                <Tab 
                    icon={<HistoryIcon />} 
                    iconPosition="start" 
                    label="Logs" 
                    id="device-tab-4"
                    aria-controls="device-tabpanel-4"
                />
                <Tab 
                    icon={<EventNoteIcon />} 
                    iconPosition="start" 
                    label="Events" 
                    id="device-tab-5"
                    aria-controls="device-tabpanel-5"
                />
            </Tabs>

            {/* Tab Panels */}
            <TabPanel value={tabIndex} index={0}>
                {/* Overview Tab */}
                <Grid container spacing={3}>
                    {/* Status Metrics */}
                    <Grid item xs={12} md={6} lg={4}>
                        <MetricCard
                            title="Lock Status"
                            value={deviceLockStatus}
                            icon={<LockIcon />}
                            status={deviceLockStatus === 'steered' ? 'success' : 'warning'}
                            tooltip="Current lock status of the device"
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <MetricCard
                            title="Valid GNSS Receivers"
                            value={deviceNGnss}
                            icon={<MemoryIcon />}
                            status={deviceNGnss > 0 ? 'info' : 'warning'}
                            tooltip="Number of GNSS satellites"
                        />
                    </Grid>
                    <Grid item xs={12} md={6} lg={4}>
                        <MetricCard
                            title="UTC / TAI"
                            value={(() => {
                                const utc = realtimeData.utc_time || null;
                                const tai = realtimeData.tai_time || null;
                                const fmt = (s: string | null) => s ? new Date(s).toLocaleTimeString(undefined, { hour12: false }) : 'N/A';
                                return `${fmt(utc)} / ${fmt(tai)}`;
                            })()}
                            icon={<AccessTimeIcon />}
                            status={'neutral'}
                            tooltip="Current UTC and TAI times (HH:mm:ss)"
                        />
                    </Grid>
                    {/* References: one card per role, detail on click */}
                    <Grid item xs={12}>
                        <ReferencesRow references={references} />
                    </Grid>
                    {/* ModoStatus reused as is but in a better styled container */}
                    <Grid item xs={12}>
                        <Paper 
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <ModoStatus device={device} />
                        </Paper>
                    </Grid>

                    {/* GNSS Receivers Summary */}
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.divider}`,
                            }}
                        >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <SatelliteIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                                    <Typography variant="h6">
                                        GNSS Receivers
                                    </Typography>
                                </Box>
                                <Button
                                    size="small"
                                    onClick={() => setTabIndex(2)}
                                    sx={{ textTransform: 'none' }}
                                >
                                    View details
                                </Button>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                                {[1, 2, 3, 4].map((rxId) => {
                                    const ubxDevice = realtimeData.ubx_telemetry?.[rxId];
                                    const fixTypeName = (() => {
                                        try {
                                            return ubxDevice?.['NAV-STATUS']?.data?.parsed_data?.fix_status?.fix_type_name ?? null;
                                        } catch { return null; }
                                    })();
                                    const constellation = rxId <= 2 ? 'GPS' : 'Galileo';
                                    const satCount = (() => {
                                        try {
                                            const counts = ubxDevice?.['NAV-SAT']?.data?.parsed_data?.constellation_summary?.constellation_counts;
                                            if (!counts) return 0;
                                            return (counts[constellation] ?? 0) as number;
                                        } catch { return 0; }
                                    })();
                                    const rfStatus = getReceiverInterferenceStatus(rxId, realtimeData.telemetry?.interference_details ?? null);

                                    return (
                                        <Grid item xs={12} sm={6} md={3} key={rxId}>
                                            <Box
                                                sx={{
                                                    p: 2,
                                                    bgcolor: 'background.default',
                                                    borderRadius: 2,
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                    RX {rxId}
                                                </Typography>
                                                <Typography variant="body1" fontWeight={600} gutterBottom>
                                                    {fixTypeName ?? 'No data'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    {satCount > 0 ? `${constellation}: ${satCount} sats` : 'No sats'}
                                                </Typography>
                                                <Chip
                                                    label={rfStatus === 'ok' ? 'OK' : rfStatus === 'jamming' ? 'JAMMING' : 'SPOOFING'}
                                                    size="small"
                                                    color={rfStatus === 'ok' ? 'success' : rfStatus === 'spoofing' ? 'error' : 'warning'}
                                                    sx={{ mt: 1, fontWeight: 600 }}
                                                />
                                            </Box>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Paper>
                    </Grid>
                </Grid>
            </TabPanel>

            <TabPanel value={tabIndex} index={1}>
                {/* Monitoring Tab */}
                <Paper
                    elevation={0}
                    sx={{
                        p: 3,
                        mb: 3,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <BarChartIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                        <Typography variant="h6">Grafana</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={2}>
                        {GRAFANA_LINKS.map((link) => (
                            <Grid item xs={12} sm={6} md={4} key={link.label}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    endIcon={<OpenInNewIcon />}
                                    component="a"
                                    href={link.build(device.mqtt_id)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        justifyContent: 'space-between',
                                        textAlign: 'left',
                                        textTransform: 'none',
                                    }}
                                >
                                    {link.label}
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h5" fontWeight={500}>
                        Real-time Data Monitoring
                    </Typography>
                    <TimeRangeSelector
                        value={timeRange}
                        onChange={(newRange) => setTimeRange(newRange)}
                    />
                </Box>
                
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <DataVisualization type="gnss" device={device} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <DataVisualization type="ocxo" device={device} />
                    </Grid>
                </Grid>
            </TabPanel>

            <TabPanel value={tabIndex} index={2}>
                {/* GNSS Receivers Tab */}
                <GnssReceiversTab device={device} />
            </TabPanel>

            <TabPanel value={tabIndex} index={3}>
                {/* Configuration Tab */}
                <ConfigTab device={device} />
            </TabPanel>

            <TabPanel value={tabIndex} index={4}>
                {/* Logs Tab */}
                <Paper 
                    elevation={0}
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    {/* Test buttons for UI logs */}
                    <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                        <Typography variant="subtitle2" sx={{ mr: 2, alignSelf: 'center' }}>
                            Test UI Logs:
                        </Typography>
                        <Button 
                            size="small" 
                            variant="outlined" 
                            color="info"
                            onClick={() => {
                                // This will be logged by the LoggingContext
                                console.log("Test info log generated");
                            }}
                        >
                            Create Info Log
                        </Button>
                        <Button 
                            size="small" 
                            variant="outlined" 
                            color="warning"
                            onClick={() => {
                                // This will be logged by the LoggingContext
                                console.warn("Test warning log generated");
                            }}
                        >
                            Create Warning Log
                        </Button>
                        <Button 
                            size="small" 
                            variant="outlined" 
                            color="error"
                            onClick={() => {
                                // This will be logged by the LoggingContext
                                console.error("Test error log generated");
                            }}
                        >
                            Create Error Log
                        </Button>
                    </Box>
                    <LogViewer device={device} />
                </Paper>
            </TabPanel>

            <TabPanel value={tabIndex} index={5}>
                {/* Events Tab: reference transitions of this device */}
                <EventsTab deviceId={device.id} />
            </TabPanel>
        </Box>
    );
};

export default DevicePage; 